import dayjs from 'dayjs';
import {
  TooltipFormatterCallback,
  TopLevelFormatterParams,
  CallbackDataParams
} from 'echarts/types/dist/shared';

const formatNumberWithCommas = (number: number) => {
  if (typeof number !== 'number') return number;
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const tooltipFormatterDefault = (
  params: CallbackDataParams[],
  dateFormatter = 'MMM DD',
  colorProperty: 'borderColor' | 'color' = 'borderColor'
) => {
  let tooltipItem = ``;
  params.forEach(el => {
    const value = Array.isArray(el.value) ? el.value[1] : el.value;
    const formattedValue =
      typeof value === 'number' ? formatNumberWithCommas(value) : value;
    tooltipItem += `<div class='ms-1'>
        <h6 class="text-body-primary fw-medium">
        <span class="d-inline-block rounded-circle me-1" style="height: 0.5rem; width: 0.5rem; background:${el[colorProperty]}"></span>
          <span class="me-3">
            ${el.seriesName} :
          </span>
          <b>${formattedValue}</b>
        </h6>
      </div>`;
  });
  return `<div>
            <p class='mb-2 text-body-tertiary'>
              ${
                dayjs(params[0].name).isValid()
                  ? dayjs(params[0].name).format(dateFormatter)
                  : params[0].name
              }
            </p>
            ${tooltipItem}
          </div>`;
};

export const tooltipFormatterCandle = (
  params: CallbackDataParams[],
  dateFormatter = 'MMM DD',
  colorProperty: 'borderColor' | 'color' = 'borderColor'
) => {
  let tooltipItem = ``;

  // Recorrer todas las series
  params.forEach(el => {
    const value = Array.isArray(el.value) ? el.value[1] : el.value; // Cerrar o volumen
    const formattedValue =
      typeof value === 'number' ? formatNumberWithCommas(value) : value;

    tooltipItem += `<div class='ms-1'>
        <h6 class="text-body-primary fw-medium">
          <span class="d-inline-block rounded-circle me-1" style="height: 0.5rem; width: 0.5rem; background:${el[colorProperty]}"></span>
          <span class="me-3">${el.seriesName} :</span>
          <b>${formattedValue}</b>
        </h6>
      </div>`;
  });

  return `<div>
            <p class='mb-2 text-body-tertiary'>
              ${
                dayjs(params[0].name).isValid()
                  ? dayjs(params[0].name).format(dateFormatter)
                  : params[0].name
              }
            </p>
            ${tooltipItem}
          </div>`;
};

export const tooltipFormatterScatter: TooltipFormatterCallback<
  TopLevelFormatterParams
> = (params: TopLevelFormatterParams) => {
  const dataParams = Array.isArray(params) ? params : [params];
  let tooltipItem = ``;

  dataParams.forEach((el: CallbackDataParams) => {
    const value = Array.isArray(el.value) ? el.value[1] : el.value;
    const formattedValue =
      typeof value === 'number'
        ? formatNumberWithCommas(Number(value.toFixed(2)))
        : value;
    tooltipItem += `<div class='ms-1'>
        <h6 class="text-body-primary fw-medium pt-2">
        <span class="d-inline-block rounded-circle me-1" style="height: 0.5rem; width: 0.5rem; background:${el.color}"></span>
        <span class="me-3">
        ${el.seriesName} :
        </span>
        <b>${formattedValue}</b>
        </h6>
      </div>`;
  });

  return `<div>
            ${tooltipItem}
          </div>`;
};

export const tooltipFormatterPie: TooltipFormatterCallback<
  TopLevelFormatterParams
> = (params: TopLevelFormatterParams) => {
  const dataParams = Array.isArray(params) ? params : [params];
  let tooltipItem = ``;

  dataParams.forEach((el: CallbackDataParams) => {
    const value = Array.isArray(el.value) ? el.value[1] : el.value;
    const formattedValue =
      typeof value === 'number'
        ? formatNumberWithCommas(Number(value.toFixed(2)))
        : value;
    tooltipItem += `<div class='ms-1'>
        <p class='mb-1 text-body-tertiary'>
        ${el.name}
        </p>
        <h6 class="text-body-primary fw-medium pt-2">
        <span class="d-inline-block rounded-circle me-1" style="height: 0.5rem; width: 0.5rem; background:${el.color}"></span>
        <span class="me-3">
        ${el.seriesName} :
        </span>
        <b>${formattedValue}%</b>
        </h6>
      </div>`;
  });

  return `<div>
            ${tooltipItem}
          </div>`;
};

export const tooltipFormatterScoreScatter: TooltipFormatterCallback<
  TopLevelFormatterParams
> = (params: TopLevelFormatterParams) => {
  const dataParams = Array.isArray(params) ? params : [params];
  let tooltipItem = ``;

  dataParams.forEach((el: CallbackDataParams) => {
    const value = Array.isArray(el.value) ? el.value[1] : el.value;
    const data = Array.isArray(el.data) ? el.data[0] : el.data;
    const formattedValue =
      typeof value === 'number'
        ? formatNumberWithCommas(Number(value.toFixed(2)))
        : value;
    const formattedData =
      typeof data === 'number'
        ? formatNumberWithCommas(Number(data.toFixed(0)))
        : data;
    tooltipItem += `<div class='ms-1'>
      <p class='mb-2 text-body-tertiary'>
      ${formattedData} calls
      </p>
      <h6 class="text-body-primary fw-medium">
      <span class="d-inline-block rounded-circle me-1" style="height: 0.5rem; width: 0.5rem; background:${el.color}"></span>
      <span class="me-3">
      ${el.seriesName} :
      </span>
      <b>${formattedValue}</b>
      </h6>
    </div>`;
  });

  return `<div>
            ${tooltipItem}
          </div>`;
};

export const tooltipFormatterList = (params: CallbackDataParams[]) => {
  const result = params.map((param, index) => {
    let label = '';
    if (dayjs(params[0].name).isValid()) {
      if (index > 0) {
        label = dayjs(params[0].name).subtract(1, 'month').format('MMM DD');
      } else {
        label = dayjs(params[0].name).format('MMM DD');
      }
    } else {
      label = params[0].name;
    }
    return {
      value: param.value,
      label,
      color: param.color
    };
  });

  let tooltipItem = ``;
  result.forEach((el, index: number) => {
    const value = Array.isArray(el.value) ? el.value[1] : el.value;
    const formattedValue =
      typeof value === 'number' ? formatNumberWithCommas(value) : value;
    tooltipItem += `<h6 class="text-body-primary fw-medium ${
      (result.length === 1 || index > 0) && 'mb-0'
    }"><span class="d-inline-block rounded-circle me-1" style="height: 0.5rem; width: 0.5rem; background:${
      el.color
    }"></span>
    <span class="me-3">
      ${el.label} :
    </span>
    <b>${formattedValue}</b>
  </h6>`;
  });

  return `<div class='ms-1'>
            ${tooltipItem}
          </div>`;
};
